import { CycleOverview } from './components/CycleOverview/CycleOverview';
import { ResolutionEfficiency } from './components/ResolutionEfficiency';
import { DashboardFilters } from './components/DashboardFilters';
import { SLAComplianceWidget } from './components/SLACompliance';
import { dashboardPageBreadcrumbs } from './config/breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import { useRtkQueryError } from './hooks/useRtkQueryError';
import type { DashboardFiltersParams } from './services';
import { useGetDashboardStatsQuery } from './services';
import {
  DashboardLayoutSA,
  DashboardLayoutCP,
  DashboardLayoutCA,
  DashboardLayoutDefault,
} from './components/DashboardSectorLayouts';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorIcon from '@mui/icons-material/Error';
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigateLinks } from '@/components';
import dayjs from 'dayjs';

export const StatisticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbs = useMemo(
    () => dashboardPageBreadcrumbs(navigate),
    [navigate]
  );
  const { getErrorMessage } = useRtkQueryError();

  const [filters, setFilters] = useState<DashboardFiltersParams>({
    dateFrom: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    dateTo: dayjs().format('YYYY-MM-DD'),
  });

  const { data, isLoading, error, refetch } =
    useGetDashboardStatsQuery(filters);

  const handleRefresh = () => {
    refetch();
  };

  const renderContent = () => {
    if (isLoading && !data) {
      return (
        <div className='flex h-96 w-full items-center justify-center bg-slate-50 p-8 rounded-xl border border-dashed border-slate-200'>
          <div className='flex flex-col items-center gap-3'>
            <CircularProgress className='!text-indigo-600' size={32} />
            <p className='text-slate-500 font-medium animate-pulse'>
              Consultando métricas...
            </p>
          </div>
        </div>
      );
    }

    if (error && !data) {
      return (
        <div className='flex h-96 w-full items-center justify-center bg-slate-50 p-8 rounded-xl border border-red-100'>
          <div className='flex flex-col items-center gap-3 text-center max-w-md'>
            <div className='bg-red-100 p-3 rounded-full'>
              <ErrorIcon className='!h-8 !w-8 text-red-600' />
            </div>
            <h3 className='text-lg font-bold text-slate-800'>
              Error al cargar datos
            </h3>
            <p className='text-slate-500'>{getErrorMessage(error)}</p>
            <button
              onClick={handleRefresh}
              className='mt-4 flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 shadow-sm rounded-lg hover:bg-slate-50 text-slate-700 font-medium transition-colors'
            >
              <ReplayIcon className='!w-4 !h-4' />
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    if (!data) return null;

    const isSA = filters.sectorId === 'SA';
    const isCP = filters.sectorId === 'CP';
    const isCA = filters.sectorId === 'CA';
    const showFailureReasons = !filters.sectorId || isCP;

    return (
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr relative z-10 transition-opacity duration-300 ${isLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
      >
        <div className='lg:col-span-1'>
          <CycleOverview data={data.cycleTime} />
        </div>

        <div className='lg:col-span-1'>
          <ResolutionEfficiency data={data.resolutionEfficiency} />
        </div>

        <div className='lg:col-span-1'>
          <SLAComplianceWidget data={data.slaCompliance} />
        </div>

        {isSA && <DashboardLayoutSA data={data} />}
        {isCP && <DashboardLayoutCP data={data} />}
        {isCA && <DashboardLayoutCA data={data} />}
        {!isSA && !isCP && !isCA && (
          <DashboardLayoutDefault
            data={data}
            showFailureReasons={showFailureReasons}
          />
        )}
      </div>
    );
  };

  return (
    <div className='min-h-screen  p-6 md:p-12 font-sans relative overflow-x-hidden'>
      <div className='absolute top-0 right-0 w-[500px] h-[500px] bg-orange-500/5 blur-[120px] rounded-full pointer-events-none' />
      <div className='absolute bottom-0 left-0 w-[500px] h-[500px] bg-amber-500/5 blur-[120px] rounded-full pointer-events-none' />

      <div className='relative z-10 mb-4'>
        <NavigateLinks actualPage='Estadísticas' breadcrumbs={breadcrumbs} />
      </div>

      <header className='mb-8 flex flex-row items-center justify-between gap-6 relative z-10'>
        <div>
          <div className='flex items-center gap-3 mb-2'>
            <div className='h-1 w-12 bg-blue-500 rounded-full' />
            <span className='text-xs font-black text-blue-600 uppercase tracking-[0.3em]'>
              Análisis de datos
            </span>
          </div>
          <h1 className='text-5xl md:text-6xl font-[1000] text-slate-900 tracking-[-0.04em] leading-tight'>
            Dashboard{' '}
            <span className='text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-500'>
              de métricas
            </span>
          </h1>
        </div>

        <div className='flex items-center gap-4 text-sm font-bold text-slate-500 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/50'>
          <div className='relative flex h-3 w-3'>
            <span
              className={`absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 ${isLoading ? 'animate-none bg-slate-400' : 'animate-ping'}`}
            ></span>
            <span
              className={`relative inline-flex rounded-full h-3 w-3 ${isLoading ? 'bg-slate-500' : 'bg-green-500'}`}
            ></span>
          </div>
          {isLoading ? 'ACTUALIZANDO...' : 'SINCRONIZADO'}
        </div>
      </header>

      <DashboardFilters onApply={setFilters} />

      {renderContent()}
    </div>
  );
};

export default StatisticsDashboard;
